import { telefonoParaMostrar } from "../../helpers/telefonoWhatsapp";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  getClubBirthdayAvailabilityApi,
  organizerAcceptBirthdayRequestApi,
  organizerGetBirthdayRequestApi,
  organizerListBirthdayRequestsApi,
  organizerListClubsApi,
  organizerProposeBirthdayRequestApi,
  organizerRejectBirthdayRequestApi,
} from "../../api/api";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import { OrganizerHeaderContext } from "../Layout";
import "../reservas/reservas-organizer.css";
import "./BirthdayInbox.css";

/* ─────────────────────────────────────────
   Bandeja de cumpleaños del club (task F2 de canchas-cumpleanos). Lista por pestañas + detalle con
   las acciones del club: aceptar, proponer otro horario o rechazar. Cada pestaña es un `status` del
   endpoint de lista (incluidos `awaiting_deposit`, `confirmed` y `closed`), con sus contadores y
   paginación del lado del back.
───────────────────────────────────────── */

const TABS = [
  { key: "pending", label: "Por responder" },
  { key: "proposed", label: "Esperando al cliente" },
  { key: "deposit", label: "Esperando seña" },
  { key: "confirmed", label: "Confirmados" },
  { key: "closed", label: "Rechazados y vencidos" },
];
const CLOSED_STATUSES = ["rejected", "expired", "cancelled"];
const STATUS_LABELS = {
  pending: "Por responder",
  proposed: "Propuesta enviada",
  accepted: "Aceptada",
  rejected: "Rechazada",
  expired: "Vencida",
  cancelled: "Cancelada por el cliente",
};
// Estado que se le pide al back por pestaña.
const TAB_STATUS = {
  pending: "pending",
  proposed: "proposed",
  deposit: "awaiting_deposit",
  confirmed: "confirmed",
  closed: "closed",
};
const PAGE_SIZE = 20;

// Ícono de los estados vacíos (torta de cumpleaños), con el trazo de los demás íconos del panel.
const EmptyIcon = () => (
  <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3v3M9 4.5V6M15 4.5V6" />
    <rect x="4" y="9" width="16" height="5" rx="2" />
    <path d="M5 14v6h14v-6M4 17c2 1.5 3.5 1.5 4.7 0 1.2 1.5 4.4 1.5 5.6 0 1.2 1.5 2.7 1.5 4.7 0" />
  </svg>
);

const EmptyState = ({ title, hint }) => (
  <div className="bdi-empty">
    <span className="bdi-empty__icon">
      <EmptyIcon />
    </span>
    <p className="bdi-empty__title">{title}</p>
    {hint ? <p className="bdi-empty__hint">{hint}</p> : null}
  </div>
);

const errorMessage = (err, fallback) => err?.response?.data?.message || fallback;
const formatDate = (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "");
const formatDeadline = (value) => (value ? dayjs(value).format("DD/MM HH:mm") : "");
const money = (value) => (Number.isFinite(Number(value)) ? `$${Number(value).toLocaleString("es-AR")}` : "");
const timeRange = (r) => `${r.slotStart}–${r.slotEnd}`;

// Pestaña a la que pertenece un pedido (con su reserva si ya se aceptó).
export const tabOfRequest = (request) => {
  if (request.status === "pending") return "pending";
  if (request.status === "proposed") return "proposed";
  if (request.status === "accepted") {
    if (request.payment === "awaiting_deposit") return "deposit";
    if (request.payment === "confirmed") return "confirmed";
    return request.reservation?.status === "pending" ? "deposit" : "confirmed";
  }
  return "closed";
};

// Link de pago para pasárselo al cliente. Si el portapapeles no está o falla, se muestra el link para
// copiarlo a mano.
function PayLink({ link }) {
  const [state, setState] = useState("idle"); // idle | copied | manual

  useEffect(() => setState("idle"), [link]);

  const copy = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("sin portapapeles");
      await navigator.clipboard.writeText(link);
      setState("copied");
    } catch (_) {
      setState("manual");
    }
  };

  return (
    <div className="bdi-paylink">
      <button type="button" className="zr-btn zr-btn--soft" onClick={copy}>
        Copiar link de pago
      </button>
      {state === "copied" ? (
        <span className="bdi-muted" role="status">
          Link copiado
        </span>
      ) : null}
      {state === "manual" ? (
        <label className="zr-field">
          <span className="zr-field__label">No pudimos copiarlo solo: copialo desde acá</span>
          <input
            type="text"
            readOnly
            className="zr-input"
            value={link}
            aria-label="Link de pago"
            onFocus={(event) => event.target.select()}
          />
        </label>
      ) : null}
    </div>
  );
}

function RequestRow({ request, active, onSelect }) {
  const shown = request.status === "proposed" && request.proposal ? request.proposal : request;
  return (
    <li>
      <button
        type="button"
        className={`bdi-row${active ? " bdi-row--active" : ""}`}
        aria-pressed={active}
        onClick={() => onSelect(request._id)}
      >
        <span className="bdi-row__top">
          <strong>{request.customer?.name || "Sin nombre"}</strong>
          <span className="bdi-row__when">
            {formatDate(shown.date)} · {timeRange(shown)}
          </span>
        </span>
        <span className="bdi-row__meta">
          {request.package?.name || "Paquete"}
          {request.status === "pending" && request.respondBy
            ? ` · responder antes del ${formatDeadline(request.respondBy)}`
            : ""}
          {CLOSED_STATUSES.includes(request.status) ? ` · ${STATUS_LABELS[request.status]}` : ""}
        </span>
      </button>
    </li>
  );
}

function Detail({ request, onChanged }) {
  const [mode, setMode] = useState(null); // null | "accept" | "propose" | "reject"
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(false);
  const [result, setResult] = useState(null);

  const [price, setPrice] = useState("");
  const [message, setMessage] = useState("");
  const [reason, setReason] = useState("");
  const [date, setDate] = useState("");
  const [starts, setStarts] = useState({ status: "idle", list: [], error: "" });
  const [slotStart, setSlotStart] = useState("");

  const packageId = request.package?._id;

  useEffect(() => {
    setMode(null);
    setError("");
    setConflict(false);
    setResult(null);
    setPrice(request.package?.price != null ? String(request.package.price) : "");
    setMessage("");
    setReason("");
    setDate("");
    setSlotStart("");
    setStarts({ status: "idle", list: [], error: "" });
  }, [request._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Horarios libres del paquete en la fecha elegida (el mismo endpoint público del cliente).
  useEffect(() => {
    if (mode !== "propose" || !date || !packageId) return undefined;
    let active = true;
    setStarts({ status: "loading", list: [], error: "" });
    setSlotStart("");
    getClubBirthdayAvailabilityApi(request.barId, { date, packageId })
      .then((response) => {
        if (!active) return;
        const list = response?.data?.info?.starts;
        setStarts({ status: "ready", list: Array.isArray(list) ? list : [], error: "" });
      })
      .catch((err) => {
        if (!active) return;
        setStarts({ status: "error", list: [], error: errorMessage(err, "No pudimos cargar los horarios.") });
      });
    return () => {
      active = false;
    };
  }, [mode, date, packageId, request.barId]);

  const optionalPrice = () => (String(price).trim() === "" ? {} : { price: Number(price) });
  const optionalMessage = () => (message.trim() ? { message: message.trim() } : {});
  const priceInvalid = String(price).trim() !== "" && !(Number(price) >= 0);

  const run = async (action, { onConflict } = {}) => {
    setBusy(true);
    setError("");
    setConflict(false);
    try {
      return await action();
    } catch (err) {
      if (err?.response?.status === 409 && onConflict) {
        onConflict(errorMessage(err, ""));
      } else {
        setError(errorMessage(err, "No pudimos completar la acción. Probá de nuevo."));
      }
      return null;
    } finally {
      setBusy(false);
    }
  };

  const accept = async () => {
    if (priceInvalid) return setError("Ingresá un precio válido.");
    const response = await run(
      () => organizerAcceptBirthdayRequestApi(request._id, { ...optionalPrice(), ...optionalMessage() }),
      { onConflict: () => setConflict(true) },
    );
    if (!response) return;
    setResult({
      kind: "accepted",
      requiresPayment: response.data?.info?.requiresPayment === true,
      payLink: response.data?.info?.payLink || null,
    });
    setMode(null);
    onChanged(request._id);
  };

  const propose = async () => {
    if (!date || !slotStart) return setError("Elegí una fecha y un horario libre.");
    if (priceInvalid) return setError("Ingresá un precio válido.");
    const response = await run(
      () =>
        organizerProposeBirthdayRequestApi(request._id, {
          date,
          slotStart,
          ...optionalPrice(),
          ...optionalMessage(),
        }),
      { onConflict: (text) => setError(text || "Ese horario ya no está libre. Elegí otro.") },
    );
    if (!response) return;
    setResult({ kind: "proposed", payLink: response.data?.info?.payLink || null });
    setMode(null);
    onChanged(request._id);
  };

  const reject = async () => {
    if (!reason.trim()) return setError("Escribí el motivo del rechazo.");
    const response = await run(() => organizerRejectBirthdayRequestApi(request._id, { reason: reason.trim() }));
    if (!response) return;
    setResult({ kind: "rejected" });
    setMode(null);
    onChanged(request._id);
  };

  // Aceptar arranca con el precio del paquete; proponer lo deja vacío (vacío = no cambia el precio).
  const open = (next) => {
    setMode(next);
    setPrice(next === "accept" && request.package?.price != null ? String(request.package.price) : "");
    setError("");
    setConflict(false);
  };

  const details = request.details || {};
  const proposal = request.status === "proposed" ? request.proposal : null;
  const payLink = result?.payLink || request.payLink || null;

  return (
    <section className="bdi-detail" aria-label="Detalle de la solicitud">
      <header className="bdi-detail__head">
        <h2>{request.package?.name || "Solicitudes de cumpleaños"}</h2>
        <span className={`bdi-badge bdi-badge--${request.status}`}>{STATUS_LABELS[request.status] || request.status}</span>
      </header>

      <dl className="bdi-facts">
        <div>
          <dt>Fecha y horario</dt>
          <dd>
            {formatDate(request.date)} · {timeRange(request)}
          </dd>
        </div>
        <div>
          <dt>Cancha</dt>
          <dd>{request.courtName || "A definir"}</dd>
        </div>
        <div>
          <dt>Cumpleañero</dt>
          <dd>{details.birthdayPerson || "—"}</dd>
        </div>
        <div>
          <dt>Edad</dt>
          <dd>{details.age ?? "—"}</dd>
        </div>
        <div>
          <dt>Invitados</dt>
          <dd>{details.guests ?? "—"}</dd>
        </div>
        <div>
          <dt>Precio del paquete</dt>
          <dd>{money(request.package?.price) || "—"}</dd>
        </div>
        {request.price != null ? (
          <div>
            <dt>Precio acordado</dt>
            <dd>{money(request.price)}</dd>
          </div>
        ) : null}
        {request.status === "pending" && request.respondBy ? (
          <div>
            <dt>Responder antes del</dt>
            <dd>{formatDeadline(request.respondBy)}</dd>
          </div>
        ) : null}
      </dl>

      {details.comments ? <p className="bdi-note">“{details.comments}”</p> : null}

      <div className="bdi-contact">
        <strong>Contacto</strong>
        <span>{request.customer?.name}</span>
        <span>{request.customer?.email}</span>
        <span>{telefonoParaMostrar(request.customer?.phone)}</span>
        {request.customer?.dni ? <span>DNI {request.customer.dni}</span> : null}
      </div>

      {proposal ? (
        <p className="bdi-note">
          Propusiste el {formatDate(proposal.date)} · {timeRange(proposal)}
          {proposal.price != null ? ` por ${money(proposal.price)}` : ""}. El cliente puede responder hasta el{" "}
          {formatDeadline(request.proposalRespondBy)}.
        </p>
      ) : null}
      {request.status === "accepted" && request.reservation ? (
        <p className="bdi-note">
          {request.reservation.status === "pending"
            ? `Esperando la seña${request.reservation.expiresAt ? ` hasta el ${formatDeadline(request.reservation.expiresAt)}` : ""}.`
            : "Reserva confirmada."}
        </p>
      ) : null}
      {payLink ? <PayLink key={request._id} link={payLink} /> : null}
      {request.status === "rejected" && request.rejectReason ? (
        <p className="bdi-note">Motivo: {request.rejectReason}</p>
      ) : null}
      {request.status === "cancelled" && request.cancelReason ? (
        <p className="bdi-note">Motivo: {request.cancelReason}</p>
      ) : null}

      {result?.kind === "accepted" ? (
        <p className="bdi-result" role="status">
          {result.requiresPayment
            ? "Aceptaste la solicitud. Le mandamos al cliente un mail con el link para pagar la seña."
            : "Aceptaste la solicitud. La reserva quedó confirmada."}
        </p>
      ) : null}
      {result?.kind === "proposed" ? (
        <p className="bdi-result" role="status">
          Mandamos la propuesta al cliente. Te avisamos cuando responda.
        </p>
      ) : null}
      {result?.kind === "rejected" ? (
        <p className="bdi-result" role="status">
          Rechazaste la solicitud. Le avisamos al cliente con tu motivo.
        </p>
      ) : null}

      {request.status === "pending" && !mode ? (
        <div className="bdi-actions">
          <button type="button" className="zr-btn zr-btn--dark" onClick={() => open("accept")}>
            Aceptar
          </button>
          <button type="button" className="zr-btn zr-btn--soft" onClick={() => open("propose")}>
            Proponer otro horario
          </button>
          <button type="button" className="zr-btn zr-btn--ghost" onClick={() => open("reject")}>
            Rechazar
          </button>
        </div>
      ) : null}

      {mode ? (
        <form
          className="bdi-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (mode === "accept") accept();
            else if (mode === "propose") propose();
            else reject();
          }}
        >
          <h3>{mode === "accept" ? "Aceptar solicitud" : mode === "propose" ? "Proponer otro horario" : "Rechazar solicitud"}</h3>

          {mode === "propose" ? (
            <>
              <label className="zr-field">
                <span className="zr-field__label">Fecha</span>
                <input
                  type="date"
                  className="zr-input"
                  value={date}
                  min={dayjs().format("YYYY-MM-DD")}
                  onChange={(event) => setDate(event.target.value)}
                />
              </label>
              {date ? (
                <div className="zr-field">
                  <span className="zr-field__label">Horarios libres</span>
                  {starts.status === "loading" ? <SpinLoading /> : null}
                  {starts.status === "error" ? <p className="bdi-error">{starts.error}</p> : null}
                  {starts.status === "ready" && starts.list.length === 0 ? (
                    <p className="bdi-muted">No hay horarios libres ese día. Probá con otra fecha.</p>
                  ) : null}
                  <div className="bdi-slots" role="group" aria-label="Horarios libres">
                    {starts.list.map((start) => (
                      <button
                        type="button"
                        key={start.slotStart}
                        className={`bdi-slot${slotStart === start.slotStart ? " bdi-slot--on" : ""}`}
                        aria-pressed={slotStart === start.slotStart}
                        onClick={() => setSlotStart(start.slotStart)}
                      >
                        {start.slotStart}–{start.slotEnd}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          ) : null}

          {mode === "reject" ? (
            <label className="zr-field">
              <span className="zr-field__label">Motivo (se lo mostramos al cliente)</span>
              <textarea
                className="zr-input zr-textarea"
                rows={3}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </label>
          ) : (
            <>
              <label className="zr-field">
                <span className="zr-field__label">Precio final {mode === "propose" ? "(opcional)" : ""}</span>
                <input
                  type="number"
                  min="0"
                  className="zr-input"
                  value={price}
                  onChange={(event) => setPrice(event.target.value)}
                />
              </label>
              <label className="zr-field">
                <span className="zr-field__label">Mensaje para el cliente (opcional)</span>
                <textarea
                  className="zr-input zr-textarea"
                  rows={3}
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                />
              </label>
            </>
          )}

          {conflict ? (
            <div className="bdi-error" role="alert">
              <p>Ese horario ya no está libre, así que no pudimos aceptar la solicitud.</p>
              <button type="button" className="zr-btn zr-btn--soft" onClick={() => open("propose")}>
                Proponer otro horario
              </button>
            </div>
          ) : null}
          {error ? (
            <p className="bdi-error" role="alert">
              {error}
            </p>
          ) : null}

          <div className="bdi-actions">
            <button type="submit" className="zr-btn zr-btn--dark" disabled={busy}>
              {mode === "accept" ? "Aceptar solicitud" : mode === "propose" ? "Enviar propuesta" : "Rechazar solicitud"}
            </button>
            <button type="button" className="zr-btn zr-btn--ghost" disabled={busy} onClick={() => setMode(null)}>
              Cancelar
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );
}

const BirthdayInbox = () => {
  const { setPageHeader } = useContext(OrganizerHeaderContext) || {};
  const [searchParams] = useSearchParams();
  const preselect = searchParams.get("solicitud");

  const [boot, setBoot] = useState({ status: "loading", error: "" });
  const [clubs, setClubs] = useState([]);
  const [clubId, setClubId] = useState("");
  const [tab, setTab] = useState("pending");
  const [counts, setCounts] = useState({});
  const [list, setList] = useState({ status: "idle", items: [], error: "" });
  const [page, setPage] = useState(1);
  const [paging, setPaging] = useState({ total: 0, limit: PAGE_SIZE });
  const [selectedId, setSelectedId] = useState(preselect || null);
  const [detail, setDetail] = useState({ status: "idle", data: null, error: "" });
  const [reloadKey, setReloadKey] = useState(0);
  const [bootKey, setBootKey] = useState(0);
  const [detailKey, setDetailKey] = useState(0);

  useEffect(() => {
    setPageHeader?.({
      title: "Cumpleaños",
      subtitle: "Solicitudes de cumpleaños de tu club.",
    });
    return () => setPageHeader?.(null);
  }, [setPageHeader]);

  // Clubes del negocio y, si llega ?solicitud=, el club y la pestaña de ese pedido.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const response = await organizerListClubsApi();
        const all = Array.isArray(response?.data?.info) ? response.data.info : [];
        if (!active) return;
        setClubs(all);
        let chosen = all[0]?._id || "";
        if (preselect) {
          try {
            const res = await organizerGetBirthdayRequestApi(preselect);
            const request = res?.data?.info;
            if (request && all.some((club) => String(club._id) === String(request.barId))) {
              chosen = String(request.barId);
              if (active) setTab(tabOfRequest(request));
            }
          } catch (_) {
            /* sin la solicitud, queda la bandeja normal */
          }
        }
        if (!active) return;
        setClubId(chosen);
        setBoot({ status: "ready", error: "" });
      } catch (err) {
        if (active) setBoot({ status: "error", error: errorMessage(err, "No pudimos cargar tus clubes.") });
      }
    })();
    return () => {
      active = false;
    };
  }, [preselect, bootKey]);

  useEffect(() => {
    if (!clubId) return undefined;
    let active = true;
    setList((prev) => ({ ...prev, status: "loading", error: "" }));
    organizerListBirthdayRequestsApi({ barId: clubId, status: TAB_STATUS[tab], page, limit: PAGE_SIZE })
      .then((response) => {
        if (!active) return;
        const info = response?.data?.info || {};
        const items = Array.isArray(info.items) ? info.items : Array.isArray(info.requests) ? info.requests : [];
        // Si la última página se vació (p. ej. tras responder un pedido), volvemos a la anterior.
        if (items.length === 0 && page > 1) return setPage(page - 1);
        setCounts(info.counts || {});
        setPaging({ total: Number(info.total) || 0, limit: Number(info.limit) || PAGE_SIZE });
        setList({ status: "ready", items, error: "" });
      })
      .catch((err) => {
        if (active) setList({ status: "error", items: [], error: errorMessage(err, "No pudimos cargar las solicitudes.") });
      });
    return () => {
      active = false;
    };
  }, [clubId, tab, page, reloadKey]);

  useEffect(() => {
    if (!selectedId) {
      setDetail({ status: "idle", data: null, error: "" });
      return undefined;
    }
    let active = true;
    setDetail({ status: "loading", data: null, error: "" });
    organizerGetBirthdayRequestApi(selectedId)
      .then((response) => active && setDetail({ status: "ready", data: response?.data?.info || null, error: "" }))
      .catch((err) => active && setDetail({ status: "error", data: null, error: errorMessage(err, "No pudimos cargar la solicitud.") }));
    return () => {
      active = false;
    };
  }, [selectedId, detailKey]);

  const tabCount = useMemo(
    () => ({
      pending: counts.pending ?? 0,
      proposed: counts.proposed ?? 0,
      deposit: counts.awaiting_deposit ?? 0,
      confirmed: counts.confirmed ?? 0,
      closed: counts.closed ?? (counts.rejected ?? 0) + (counts.expired ?? 0) + (counts.cancelled ?? 0),
    }),
    [counts],
  );
  const pages = Math.max(1, Math.ceil(paging.total / paging.limit));

  const refresh = () => setReloadKey((key) => key + 1);

  // Tras una acción el pedido cambia de pestaña: recargamos listas y detalle sin cerrar el resultado.
  const handleChanged = useCallback(() => {
    refresh();
    organizerGetBirthdayRequestApi(selectedId)
      .then((response) => setDetail((prev) => ({ ...prev, data: response?.data?.info || prev.data })))
      .catch(() => {});
  }, [selectedId]);

  if (boot.status === "loading") return <SpinLoading />;
  if (boot.status === "error") {
    return (
      <div className="bdi bdi-state" role="alert">
        <p>{boot.error}</p>
        <button type="button" className="zr-btn zr-btn--dark" onClick={() => setBootKey((key) => key + 1)}>
          Reintentar
        </button>
      </div>
    );
  }
  if (clubs.length === 0) {
    return (
      <div className="bdi bdi-state">
        <p>Todavía no tenés un club. Creá uno en Canchas para recibir cumpleaños.</p>
      </div>
    );
  }

  return (
    <div className="bdi">
      {clubs.length > 1 ? (
        <label className="zr-field bdi-club">
          <span className="zr-field__label">Club</span>
          <select
            className="zr-input"
            value={clubId}
            onChange={(event) => {
              setClubId(event.target.value);
              setPage(1);
              setSelectedId(null);
            }}
          >
            {clubs.map((club) => (
              <option key={club._id} value={club._id}>
                {club.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <div className="zr-tabs bdi-tabs" role="tablist" aria-label="Estados de las solicitudes">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`zr-tabs__item${tab === key ? " zr-tabs__item--active" : ""}`}
            onClick={() => {
              setTab(key);
              setPage(1);
              setSelectedId(null);
            }}
          >
            {label} <span className="bdi-tab__count">{tabCount[key]}</span>
          </button>
        ))}
      </div>

      <div className="bdi-layout">
        <div className="bdi-list bdi-card">
          {list.status === "loading" ? <SpinLoading /> : null}
          {list.status === "error" ? (
            <div className="bdi-state" role="alert">
              <p>{list.error}</p>
              <button type="button" className="zr-btn zr-btn--dark" onClick={refresh}>
                Reintentar
              </button>
            </div>
          ) : null}
          {list.status === "ready" && list.items.length === 0 ? (
            <EmptyState
              title="No hay solicitudes en esta pestaña."
              hint="Cuando un cliente pida un cumpleaños, lo vas a ver acá."
            />
          ) : null}
          {list.status === "ready" && list.items.length > 0 ? (
            <ul className="bdi-rows">
              {list.items.map((request) => (
                <RequestRow
                  key={request._id}
                  request={request}
                  active={String(selectedId) === String(request._id)}
                  onSelect={setSelectedId}
                />
              ))}
            </ul>
          ) : null}
          {list.status === "ready" && pages > 1 ? (
            <nav className="bdi-pager" aria-label="Paginación">
              <button type="button" className="zr-btn zr-btn--ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                Anterior
              </button>
              <span className="bdi-muted">
                Página {page} de {pages}
              </span>
              <button type="button" className="zr-btn zr-btn--ghost" disabled={page >= pages} onClick={() => setPage(page + 1)}>
                Siguiente
              </button>
            </nav>
          ) : null}
        </div>

        <div className="bdi-side bdi-card">
          {!selectedId ? (
            <EmptyState title="Elegí una solicitud para ver el detalle." hint="Acá vas a poder aceptarla, proponer otro horario o rechazarla." />
          ) : null}
          {detail.status === "loading" ? <SpinLoading /> : null}
          {detail.status === "error" ? (
            <div className="bdi-state" role="alert">
              <p>{detail.error}</p>
              <button type="button" className="zr-btn zr-btn--dark" onClick={() => setDetailKey((key) => key + 1)}>
                Reintentar
              </button>
            </div>
          ) : null}
          {detail.status === "ready" && detail.data ? (
            <Detail request={detail.data} onChanged={handleChanged} />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default BirthdayInbox;
